import { useState } from 'react';
import { ArrowLeft, Code2, Copy, Eye, X } from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  HEADER_ICON_BUTTON,
  SURFACE_PAGE_TAB_ACTIVE,
  SURFACE_PAGE_TAB_INACTIVE,
} from '@/shared/ui-class';
import type { SeedArtifact } from '@/versions/v1/shared/files-seed';

type ViewerTab = 'preview' | 'code';

export function ArtifactViewerPanel({
  artifact,
  onBack,
  onClose,
}: {
  artifact: SeedArtifact;
  onBack: () => void;
  onClose?: () => void;
}) {
  const [tab, setTab] = useState<ViewerTab>(
    artifact.kind === 'code' ? 'code' : 'preview',
  );
  const [version, setVersion] = useState(artifact.version);

  const content = tab === 'code' ? artifact.codeContent : artifact.previewContent;

  const copy = () => {
    void navigator.clipboard.writeText(content);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-divider px-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to artifacts list"
          title="Back"
          className={HEADER_ICON_BUTTON}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
            {artifact.name}
          </div>
          <div className="truncate text-xs text-muted">{artifact.meta}</div>
        </div>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy content"
          title="Copy"
          className={HEADER_ICON_BUTTON}
        >
          <Copy className="h-4 w-4" />
        </button>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close files pane"
            title="Close"
            className={HEADER_ICON_BUTTON}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </header>

      <div className="flex items-center justify-between gap-2 border-b border-divider px-3 py-2">
        <div className="flex items-center gap-1 rounded-full bg-[var(--glass-surface-soft)] p-0.5">
          <button
            type="button"
            onClick={() => setTab('preview')}
            className={cn(
              'inline-flex items-center gap-1',
              tab === 'preview' ? SURFACE_PAGE_TAB_ACTIVE : SURFACE_PAGE_TAB_INACTIVE,
            )}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </button>
          <button
            type="button"
            onClick={() => setTab('code')}
            className={cn(
              'inline-flex items-center gap-1',
              tab === 'code' ? SURFACE_PAGE_TAB_ACTIVE : SURFACE_PAGE_TAB_INACTIVE,
            )}
          >
            <Code2 className="h-3.5 w-3.5" />
            Code
          </button>
        </div>
        <VersionChip
          version={version}
          maxVersion={artifact.version}
          onChange={setVersion}
        />
      </div>

      <div className="scrollbar-subtle min-h-0 flex-1 overflow-y-auto p-4">
        {tab === 'preview' ? (
          <div className="whitespace-pre-wrap text-sm text-zinc-900 dark:text-zinc-100">
            {content}
          </div>
        ) : (
          <pre className="rounded-lg border border-[var(--glass-border)] bg-zinc-50 p-3 text-xs leading-relaxed text-zinc-800 dark:bg-[color-mix(in_srgb,var(--color-zinc-900)_60%,transparent)] dark:text-zinc-100">
            <code>{content}</code>
          </pre>
        )}
      </div>
    </div>
  );
}

function VersionChip({
  version,
  maxVersion,
  onChange,
}: {
  version: number;
  maxVersion: number;
  onChange: (v: number) => void;
}) {
  if (maxVersion <= 1) {
    return (
      <span className="rounded-full border border-[var(--glass-border)] px-2 py-0.5 text-xs text-muted">
        v{version}
      </span>
    );
  }

  return (
    <select
      value={version}
      onChange={(event) => onChange(Number(event.target.value))}
      aria-label="Artifact version"
      className="rounded-full border border-[var(--glass-border)] bg-transparent px-2 py-0.5 text-xs text-zinc-700 dark:text-zinc-200"
    >
      {Array.from({ length: maxVersion }, (_, index) => maxVersion - index).map((v) => (
        <option key={v} value={v}>
          v{v}
        </option>
      ))}
    </select>
  );
}
